// Field type configurations
export const FIELD_TYPES = {
  // Basic types
  text: 'text',
  number: 'number',
  date: 'date',
  select: 'select',
  tag: 'tag',
  multiselect: 'multiselect',
  boolean: 'boolean',
  
  // Special types
  number_range: 'number_range',
  date_range: 'date_range',
};

// Field types and their corresponding operators
export const OPERATORS = {
  string: [
    { label: 'is', value: 'equals' },
    { label: 'is not', value: 'not_equals' },
  ],
  number: [
    { label: 'equal to', value: 'equals' },
    { label: 'not equal to', value: 'not_equals' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'greater than or equal to', value: 'greater_than_or_equal' },
    { label: 'less than', value: 'less_than' },
    { label: 'less than or equal to', value: 'less_than_or_equal' },
  ],
  date: [
    { label: 'is', value: 'equals' },
    { label: 'is not', value: 'not_equals' },
    { label: 'is after', value: 'gt' },
    { label: 'is on or after', value: 'gte' },
    { label: 'is before', value: 'lt' },
    { label: 'is on or before', value: 'lte' },
  ],
  select: [
    { label: 'is', value: 'equals' },
    { label: 'is not', value: 'not_equals' },
  ],
  multiselect: [
    { label: 'contains', value: 'contains' },
    { label: 'is not', value: 'not_equals' },
  ],
  boolean: [
    { label: 'is', value: 'equals' },
    { label: 'is not', value: 'not_equals' },
  ],
  date_range: [
    { label: 'is between', value: 'between' },
    { label: 'is not between', value: 'not_between' }
  ],
  number_range: [
    { label: 'equal to', value: 'equals' },
    { label: 'not equal to', value: 'not_equals' }
  ],
  text: [
    { label: 'is', value: 'equals' },
    { label: 'is not', value: 'not_equals' }
  ],
};

// Special field configurations
export const SPECIAL_FIELDS = {
  // Monthly Salary options
  monthly_salary: [
    { label: 'Less than 25,000', value: 'lt_25000' },
    { label: '25,000 - 35,000', value: '25000_35000' },
    { label: '35,000 - 45,000', value: '35000_45000' },
    { label: '45,000 - 55,000', value: '45000_55000' },
    { label: '55,000 - 75,000', value: '55000_75000' },
    { label: '75,000 - 1,00,000', value: '75000_100000' },
    { label: 'Greater than 1,00,000', value: 'gt_100000' },
  ],
  
  // Annual Income options
  annual_income: [
    { label: 'Less than 1,00,000', value: 'lt_100000' },
    { label: '1,00,000 - 2,00,000', value: '100000_200000' },
    { label: '2,00,000 - 3,00,000', value: '200000_300000' },
    { label: '3,00,000 - 5,00,000', value: '300000_500000' },
    { label: '5,00,000 - 7,50,000', value: '500000_750000' },
    { label: '7,50,000 - 10,00,000', value: '750000_1000000' },
    { label: 'Greater than 10,00,000', value: 'gt_1000000' },
  ],
  
  // Birth date options (1-31)
  birth_date: Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  })),
  
  // Birth year options (current year - 100 to current year)
  birth_year: (() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, i) => ({
      label: (currentYear - i).toString(),
      value: (currentYear - i).toString(),
    }));
  })(),
};

// Special criteria that are fields themselves
export const FIELD_AS_CRITERIA = [
  'tags',
  'contact_origin',
  'contact_source',
  'contact_category',
];

/**
 * Get field options based on field ID and type
 * @param {string} fieldId - The ID of the field
 * @param {string} fieldType - The type of the field (text, number, date, etc.)
 * @param {Array|Object} options - The options for the field
 * @returns {Array} - Array of {label, value} objects
 */
export const getFieldOptions = (fieldId, fieldType, options = []) => {
  try {
    // Handle empty or invalid options
    if (!options) return [];
    
    // If options is an array of strings (like months)
    if (Array.isArray(options) && options.every(opt => typeof opt === 'string')) {
      return options.map((opt,index) => ({
        label: opt,
        value: opt,
        key: index, // Add key for React's reconciliation
      }));
    }
    
    // If options is an array of objects with name/id
    if (Array.isArray(options) && options.every(opt => opt && typeof opt === 'object')) {
      return options.map(opt => ({
        label: opt.name || opt.label || String(opt.id || opt.value || ''),
        value: String(opt.name || opt.value || '')
      }));
    }
    
    // If options is an object with id/name pairs
    if (options && typeof options === 'object' && !Array.isArray(options)) {
      return Object.entries(options).map(([value, label]) => ({
        label: label || value,
        value: value
      }));
    }
    
    // Default empty array for any other case
    return [];
  } catch (error) {
    console.error('Error in getFieldOptions:', { fieldId, fieldType, options, error });
    return [];
  }
};

/**
 * Get operators for a field type
 * @param {string} fieldType - The type of the field
 * @returns {Array} - Array of operator objects with label and value
 */
export const getOperators = (fieldType) => {
  // Handle null/undefined
  if (!fieldType) return OPERATORS.string;
  
  // Handle special field types
  const lowerFieldType = String(fieldType).toLowerCase();
  
  // Map field types to operator groups
  const operatorMap = {
    // Text fields
    text: 'string',
    string: 'string',
    email: 'string',
    phone: 'string',
    
    // Numeric fields
    number: 'number',
    integer: 'number',
    float: 'number',
    
    // Date fields
    date: 'date',
    datetime: 'date',
    time: 'date',
    
    // Selection fields
    select: 'select',
    radio: 'select',
    dropdown: 'select',
    
    // Multi-selection fields
    multiselect: 'multiselect',
    checkbox: 'multiselect',
    tags: 'multiselect',
    
    // Boolean fields
    boolean: 'boolean',
    switch: 'boolean',
    
    // Range fields
    date_range: 'date_range',
    number_range: 'number_range',
    range: 'number_range',
  };
  
  // Get the operator group, default to string
  const operatorGroup = operatorMap[lowerFieldType] || 'string';
  
  // Return the operators for this group
  return OPERATORS[operatorGroup] || OPERATORS.string;
};

/**
 * Check if a criteria is a special field that requires special handling
 * @param {string} criteria - The criteria key from the API response
 * @returns {boolean} - True if it's a special criteria
 */
export const isSpecialCriteria = (criteria) => {
  if (!criteria) return false;
  
  const specialCriteria = [
    'tags',
    'contact_origin',
    'contact_source',
    'contact_category'
  ];
  
  return specialCriteria.includes(criteria);
};
/**
 * Transform the API response into a format suitable for the criteria selector
 * @param {Object} apiResponse - The raw API response
 * @returns {Array} - Transformed criteria options
 */
export const transformCriteriaOptions = (apiResponse) => {
  if (!apiResponse || typeof apiResponse !== 'object') return [];
  
  return Object.entries(apiResponse).map(([criteria, fields]) => ({
    label: criteria.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
    value: criteria,
    isSpecial: isSpecialCriteria(criteria),
    fields: Array.isArray(fields) ? fields : []
  }));
};

/**
 * Get the default operator for a field type
 * @param {string} fieldType - The type of the field
 * @returns {string} - The default operator value
 */
export const getDefaultOperator = (fieldType) => {
  const operators = getOperators(fieldType);
  return operators.length > 0 ? operators[0].value : 'is';
};

/**
 * Get the default value for a field type
 * @param {string} fieldType - The type of the field
 * @returns {any} - The default value
 */
export const getDefaultValue = (fieldType) => {
  switch (fieldType) {
    case 'number':
    case 'integer':
    case 'float':
      return 0;
    case 'boolean':
      return true;
    case 'date':
      return new Date().toISOString().split('T')[0];
    case 'select':
    case 'multiselect':
      return [];
    default:
      return '';
  }
};

/**
 * Format a condition for display in the UI
 * @param {Object} condition - The condition object
 * @param {Object} context - Additional context (e.g., field definitions)
 * @returns {string} - Formatted display text
 */
export const formatCondition = (condition, context = {}) => {
  const { criteria, field, operator, value, field_type: fieldType } = condition;
  const { fieldDefinitions = {} } = context;
  
  let displayText = '';
  
  // Add criteria name if it's not a special criteria
  if (!isSpecialCriteria(criteria)) {
    displayText += `${formatLabel(criteria)}: `;
  }
  
  // Add field name if it exists
  if (field) {
    const fieldDef = fieldDefinitions[field] || {};
    displayText += `${fieldDef.name || field} `;
  }
  
  // Add operator
  const operatorLabel = getOperatorLabel(operator, fieldType);
  displayText += `${operatorLabel} `;
  
  // Format value based on field type
  if (value === undefined || value === null || value === '') {
    displayText += 'empty';
  } else if (Array.isArray(value)) {
    // Handle array values (e.g., multiselect, between)
    if (fieldType === 'date_range' || fieldType === 'number_range') {
      displayText += `${value[0]} and ${value[1]}`;
    } else {
      displayText += value.join(', ');
    }
  } else if (fieldType === 'boolean') {
    displayText += value ? 'Yes' : 'No';
  } else if (fieldType === 'date') {
    displayText += new Date(value).toLocaleDateString();
  } else {
    // Handle select options
    if (field && fieldDefinitions[field]?.options) {
      const option = fieldDefinitions[field].options.find(
        opt => String(opt.value) === String(value)
      );
      displayText += option ? option.label : value;
    } else {
      displayText += String(value);
    }
  }
  
  return displayText.trim();
};

/**
 * Format a label by converting snake_case to Title Case
 * @param {string} str - The string to format
 * @returns {string} - Formatted label
 */
const formatLabel = (str) => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get the display label for an operator
 * @param {string} operator - The operator value
 * @param {string} fieldType - The field type
 * @returns {string} - The operator display label
 */
const getOperatorLabel = (operator, fieldType) => {
  // Find the operator in any of the operator groups
  for (const group of Object.values(OPERATORS)) {
    const op = group.find(op => op.value === operator);
    if (op) return op.label;
  }
  
  // Default to the operator value if not found
  return operator || 'is';
};

// Format text by capitalizing first letter and replacing underscores with spaces
const formatDisplayText = (text) => {
  if (!text) return '';
  // Replace underscores with spaces and capitalize first letter of each word
  return text
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

export { getOperatorLabel, formatDisplayText };
